import { motion } from "framer-motion";
import { Square, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";

interface VoiceRecordingIndicatorProps {
  isRecording: boolean;
  isTranscribing: boolean;
  isStarting?: boolean;
  isReviewing?: boolean;
  seconds: number;
  amplitude: number;
  onCancel: () => void;
  onStop: () => void;
}

const formatTime = (seconds: number) => {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${(safe % 60).toString().padStart(2, "0")}`;
};

// Normal document flow, across the whole composer. Positioning this above a
// narrow microphone button clips the label at the right edge on mobile Safari.
export const VoiceRecordingIndicator = ({
  isRecording, isTranscribing, isStarting = false, isReviewing = false,
  seconds, amplitude, onCancel, onStop,
}: VoiceRecordingIndicatorProps) => {
  const { t } = useLanguage();
  if (!isRecording && !isTranscribing && !isStarting && !isReviewing) return null;
  const status = isTranscribing ? "transcribing" : isStarting ? "starting" : isRecording ? "recording" : "review";
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="flex basis-full w-full min-w-0 items-center gap-1 rounded-2xl bg-primary/5 px-1"
      data-voice-capture-status={status}
    >
      <button type="button" onClick={onCancel}
        aria-label={t("mirror.voice.cancel")} title={t("mirror.voice.cancel")}
        className="min-h-11 min-w-11 shrink-0 rounded-xl flex items-center justify-center text-foreground/70 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        <X size={18} aria-hidden="true" />
      </button>
      <div className="flex flex-1 min-w-0 items-center justify-center gap-2 py-2 text-primary">
        {isRecording && <div aria-hidden="true" className="flex shrink-0 items-center gap-0.5 h-5">
          {[0, 1, 2, 3, 4].map(i => <motion.span key={i}
            animate={{ scaleY: Math.max(0.2, 0.3 + Math.min(1, amplitude) * (0.6 + i % 3 * 0.2)) }}
            transition={{ duration: 0.1 }} className="block h-4 w-0.5 rounded bg-primary" />)}
        </div>}
        <span role="status" aria-live="polite" aria-atomic="true" className="min-w-0 text-center text-xs leading-4">
          {t(`mirror.voice.${status}`)}
        </span>
        {isRecording && <span aria-hidden="true" className="shrink-0 font-mono text-xs tabular-nums">{formatTime(seconds)}</span>}
      </div>
      {isRecording && !isTranscribing && !isStarting ? <button type="button" onClick={onStop}
        aria-label={t("mirror.voice.stopRecording")} title={t("mirror.voice.stopRecording")}
        className="min-h-11 min-w-11 shrink-0 rounded-xl flex items-center justify-center bg-primary/15 text-primary hover:bg-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
        <Square size={14} fill="currentColor" aria-hidden="true" />
      </button> : <div aria-hidden="true" className="w-11 shrink-0" />}
    </motion.div>
  );
};
